<template>
	<InlineModal
		open
		:variant="isShowingCalculator ? 'adaptive' : 'small'"
		@update:open="handleModalClose"
	>
		<template #header>
			<div class="mb-4 flex w-full items-center justify-between">
				<h1 class="text-lg font-bold">
					{{
						isShowingConfirmation
							? ''
							: t('document.changePaymentDetailsModal.title')
					}}
				</h1>
				<button
					type="button"
					@click="closeModal"
				>
					<XMarkIcon class="h-6 w-6 text-slate-300!" />
				</button>
			</div>
		</template>

		<!-- Calculator View -->
		<PaymentOptionsCalculator
			v-show="isShowingCalculator"
			v-model:stagedChanges="stagedChanges"
			:selectedPlanId="sessionDocumentsStore.currentPlanHolderID"
		/>

		<!-- Confirmation Warning View -->
		<div
			v-if="isShowingConfirmation"
			class="flex flex-col items-center justify-center gap-2 px-4 text-center"
			aria-label="Confirmation Warning View"
		>
			<ExclamationTriangleIcon class="h-10 w-10 text-red-500!" />
			<h1 class="text-lg font-bold">
				{{ t('document.changePaymentDetailsModal.confirmationTitle') }}
			</h1>
			<p class="text-center text-sm text-slate-500!">
				{{
					t('document.changePaymentDetailsModal.message', {
						forms: formsToInvalidate.join(', '),
					})
				}}
			</p>
		</div>

		<!-- Modal Footer -->
		<div class="flex flex-col items-center justify-center gap-2 pt-4">
			<button
				type="button"
				aria-label="Confirm Payment Changes"
				class="bg-accent-400! text-accent-700! hover:bg-accent-500! hover:text-accent-800! mb-2 w-3/4 rounded-md py-2 font-bold first-letter:rounded-lg disabled:bg-slate-300! disabled:text-slate-500/50!"
				@click="handleConfirmButton"
				:disabled="!hasStagedChanges"
			>
				{{ t('document.confirm') }}
			</button>
			<button
				type="button"
				aria-label="Cancel Payment Changes"
				class="font-bold text-red-500! underline!"
				@click="handleCancel"
			>
				{{ t('document.resetFormAbort') }}
			</button>
		</div>
	</InlineModal>
</template>

<script setup lang="ts">
import InlineModal from '@/components/common/InlineModal.vue'
import PaymentOptionsCalculator from '@/components/documents/Foxit/ChangePaymentDetails/PaymentOptionsCalculator.vue'
import { DocumentType } from '@/enums/DocumentType'
import { usePresentationLocale } from '@/i18n'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore, type Step } from '@/stores/sessionDocuments'
import type { PlanData } from '@/types'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { onMounted, ref, computed } from 'vue'

// Constants for modal states
const MODAL_STATE = {
	CALCULATOR: 'calculator',
	CONFIRMATION: 'changePaymentTypeConfirmation',
} as const

type ModalState = (typeof MODAL_STATE)[keyof typeof MODAL_STATE]

const emit = defineEmits(['confirmPayment', 'closeModal'])
const planStore = usePlanStore()
const sessionDocumentsStore = useSessionDocumentsStore()
const sessionStore = useSessionStore()
const { t } = usePresentationLocale()

// Modal state
const currentModalState = ref<ModalState>(MODAL_STATE.CALCULATOR)

const stagedChanges = ref<Partial<PlanData>>({})

defineExpose({
	currentModalState,
	stagedChanges,
})

// Computed properties
const paymentAuthorizationStep = computed(
	(): Step | undefined =>
		sessionDocumentsStore.getStepByLabel(DocumentType.PaymentAuthorization) ??
		undefined
)

const goodsAndServicesStep = computed(
	(): Step | undefined =>
		sessionDocumentsStore.getStepByLabel(
			DocumentType.StatementOfGoodsAndServices
		) ?? undefined
)

const enrollmentStep = computed(
	(): Step | undefined =>
		sessionDocumentsStore.getStepByLabel(DocumentType.EnrollmentForm) ??
		undefined
)

const isShowingCalculator = computed(
	() => currentModalState.value === MODAL_STATE.CALCULATOR
)

const isShowingConfirmation = computed(
	() => currentModalState.value === MODAL_STATE.CONFIRMATION
)

const hasStagedChanges = computed(
	() => Object.keys(stagedChanges.value).length > 0
)

onMounted(() => {
	currentModalState.value = MODAL_STATE.CALCULATOR
})

// In the case of texas, also include enrollment or goods and services step since they both have payment info.
const formsToInvalidate = computed<string[]>(() => {
	const result = []
	const isTexas = sessionStore.activeFh?.billingAddress?.stateCode === 'TX'

	if (isTexas && goodsAndServicesStep.value?.completed) {
		result.push(goodsAndServicesStep.value.label)
	}

	if (isTexas && enrollmentStep.value?.completed) {
		result.push(enrollmentStep.value.label)
	}

	if (paymentAuthorizationStep.value?.completed) {
		result.push(paymentAuthorizationStep.value.label)
	}

	return result
})

/**
 * Handles the confirm button click.
 * If payment step is completed, shows confirmation warning first.
 * Otherwise, commits changes immediately.
 */
const handleConfirmButton = async () => {
	if (formsToInvalidate.value.length > 0 && isShowingCalculator.value) {
		showConfirmationWarning()
	} else {
		await commitChangesAndClose()
	}
}

/**
 * Shows the confirmation warning screen.
 * This warns users that changing payment details will invalidate
 * the Payment Authorization Form.
 */
const showConfirmationWarning = () => {
	currentModalState.value = MODAL_STATE.CONFIRMATION
}

/**
 * Commits staged changes to the store and closes the modal.
 * Mark steps as incomplete and autofill to false if payment details have changed.
 */
const commitChangesAndClose = async () => {
	const stepsToMarkAsIncomplete = [
		paymentAuthorizationStep.value,
		enrollmentStep.value,
	]

	if (sessionStore.activeFh?.billingAddress?.stateCode === 'TX') {
		stepsToMarkAsIncomplete.push(goodsAndServicesStep.value)
	}

	for (const step of stepsToMarkAsIncomplete) {
		if (step) {
			await sessionDocumentsStore.setStepCompleted(step, false)
			sessionDocumentsStore.setStepAutoFilled(step, false)
		}
	}

	if (Object.keys(stagedChanges.value).length > 0) {
		await planStore.updatePlan(
			sessionDocumentsStore.currentPlanHolderID,
			stagedChanges.value
		)
	}

	closeModal()
	emit('confirmPayment')
}

/**
 * Handles cancel/back button clicks.
 * If showing confirmation, goes back to calculator.
 * Otherwise, resets staged changes and closes modal.
 */
const handleCancel = () => {
	if (isShowingConfirmation.value) {
		// Go back to calculator view
		currentModalState.value = MODAL_STATE.CALCULATOR
	} else {
		closeModal()
	}
}

/**
 * Closes the modal and resets state.
 */
const closeModal = () => {
	emit('closeModal')
	currentModalState.value = MODAL_STATE.CALCULATOR
}

/**
 * Handles modal close event (e.g., clicking outside or X button).
 * Resets staged changes if modal is closed without confirming.
 */
const handleModalClose = (isOpen: boolean) => {
	if (!isOpen) {
		handleCancel()
	}
}
</script>
